/**
 * A calendar date with no instant attached.
 *
 * ## Why this is its own module
 *
 * A date picker deals in *civil* dates — "the first of August" — and every date
 * API in the platform deals in instants. The two are a different day for half the
 * world at any given moment, and the gap is where date pickers go wrong:
 * `new Date("2026-08-01")` is midnight UTC, which is already the first in Phnom
 * Penh and still 31 July in Los Angeles. Hand that to a calendar reading the
 * runtime zone and the operator watches the wrong day light up.
 *
 * So a civil date is carried as its own three numbers, and the only `Date` it is
 * ever turned into is UTC midnight — which the calendar is told to read as UTC,
 * making the round trip exact and zone-independent. Turning a civil date into a
 * real instant is a separate question that needs a zone and a time of day, and it
 * stays in `usageWindow`, which already answers it and has the DST tests to prove
 * it.
 *
 * Two pickers now sit on this: the fleet views' custom energy window, and a
 * card's expiry date. `usageWindow` owned all of it privately until the second
 * one appeared.
 */

export interface CivilDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/** What `<input type="date">` produces and what the URL carries. */
const CIVIL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * `absent` and `invalid` are different answers and callers say different things
 * about them: one is a field still to fill in, the other is a URL that cannot be
 * honoured. A single `undefined` would report a hand-edited date as an empty
 * form.
 */
export function parseCivilDate(raw: string | null): CivilDate | "absent" | "invalid" {
  if (raw === null || raw === "") return "absent";

  const match = CIVIL_DATE_PATTERN.exec(raw);
  if (match === null) return "invalid";

  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];

  // The pattern accepts 2026-02-31; the calendar does not. `Date.UTC` normalises
  // it to 3 March, so round-tripping is what catches it.
  const at = new Date(Date.UTC(year, month - 1, day));
  const roundTrips =
    at.getUTCFullYear() === year && at.getUTCMonth() === month - 1 && at.getUTCDate() === day;

  return roundTrips ? { year, month, day } : "invalid";
}

/** Back to the wire shape, padded, for the URL and for a native date input. */
export function formatCivilDate(date: CivilDate): string {
  const month = String(date.month).padStart(2, "0");
  const day = String(date.day).padStart(2, "0");

  return `${date.year}-${month}-${day}`;
}

/**
 * The instant the calendar is given for this date, and the only `Date` a civil
 * date is ever turned into here.
 *
 * `day` may be out of range: `usageWindow` routinely asks for the 32nd or the
 * 0th, and `Date.UTC` normalising it is exactly the month arithmetic that wants.
 */
export function civilDateAsUtcDate(date: CivilDate): Date {
  return new Date(Date.UTC(date.year, date.month - 1, date.day));
}

/**
 * What the calendar hands back. Read with the UTC getters, matching
 * `civilDateAsUtcDate` and the `timeZone="UTC"` the picker runs with — local
 * getters here would move the date a day for every operator behind UTC.
 */
export function civilDateFromUtcDate(at: Date): CivilDate {
  return { year: at.getUTCFullYear(), month: at.getUTCMonth() + 1, day: at.getUTCDate() };
}

const PARTS = new Map<string, Intl.DateTimeFormat>();

/** Cached: these are constructed per render otherwise, and `Intl` is not cheap. */
function formatter(zone: string): Intl.DateTimeFormat {
  const existing = PARTS.get(zone);
  if (existing !== undefined) return existing;

  const created = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  PARTS.set(zone, created);
  return created;
}

/** What the wall clock in `zone` reads at this instant. */
export function civilDateInZone(zone: string, at: Date): CivilDate {
  const parts = new Map(
    formatter(zone)
      .formatToParts(at)
      .map((part) => [part.type, part.value]),
  );

  return {
    year: Number(parts.get("year")),
    month: Number(parts.get("month")),
    day: Number(parts.get("day")),
  };
}

/**
 * The zone's offset from UTC at an instant, in milliseconds.
 *
 * Derived by reading the wall clock and subtracting: `Date.UTC` of the local
 * parts, minus the instant itself, is exactly the offset. Preferred over parsing
 * `timeZoneName: "longOffset"` because it needs no string handling and cannot be
 * defeated by a zone whose name renders as "GMT+7" in one engine and "UTC+07:00"
 * in another.
 */
export function zoneOffsetMs(zone: string, at: Date): number {
  const parts = new Map(
    formatter(zone)
      .formatToParts(at)
      .map((part) => [part.type, part.value]),
  );

  const asUtc = Date.UTC(
    Number(parts.get("year")),
    Number(parts.get("month")) - 1,
    Number(parts.get("day")),
    Number(parts.get("hour")) % 24,
    Number(parts.get("minute")),
    Number(parts.get("second")),
  );

  // Seconds resolution is all `formatToParts` gives, so drop the instant's
  // milliseconds rather than letting them show up as an offset that is 999 ms off.
  return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}

/**
 * "1 Mar 2026". Formatted in UTC because the parts being labelled are already
 * the operator's own — a formatter left on the runtime zone would shift the
 * label a day for half the world.
 */
const DAY_LABEL = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
  year: "numeric",
});

export function formatCivilDateLabel(date: CivilDate): string {
  return DAY_LABEL.format(civilDateAsUtcDate(date));
}
